/* Copyright (c) 2020-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Observable } from 'rxjs';
import { BaseConfigType } from 'app-config/lib/app.config';
import { OpenTelemetrySpan } from 'distributed-tracing/lib/distributed-tracing';
import { Session } from './models/session.model';
/* interfaces */
import {
    StarfleetUser,
    StarfleetTransitionState,
    KVStoreClientDataResponse,
    ErrorInfo,
    OAuthLoginData,
    DeviceDisplayCodeData,
    AuthStatus,
    LoginOptions
} from './starfleet.interface';

export abstract class StarfleetService {
    constructor() {}
    // must be the first function called, before anything else
    public abstract initialize(useCachedSession: boolean, useCachedUser: boolean, id_token?: string): void;
    /**
     * Triggers a login dialog to be shown
     *
     * @param force Forces the login dialog to be shown even if already logged in.
     * @param deviceName of the device the user will be logging onto. Used in off-device flow.
     */
    public abstract login(options?: LoginOptions): Observable<StarfleetTransitionState>;
    public abstract cancelLogin(stopHttpServer: boolean): Observable<OAuthLoginData>;
    public abstract logout(
        accessToken?: string,
        logOutNotSupportedUrl?: string,
        returnLocationUrl?: string,
        isSilentLogout?: boolean
    ): Observable<StarfleetTransitionState>;
    public abstract refreshUser(): Observable<StarfleetUser>;
    public abstract getAuthToken(refreshForExpired?: boolean, parentSpan?: OpenTelemetrySpan): Observable<string>;
    public abstract getClientData(
        keys: string[],
        parentSpan?: OpenTelemetrySpan
    ): Observable<KVStoreClientDataResponse>;
    public abstract setClientData(key: string, value: string[], parentSpan?: OpenTelemetrySpan): Observable<any>;
    public abstract appendClientData(key: string, value: string[], parentSpan?: OpenTelemetrySpan): Observable<any>;
    public abstract deleteClientData(key: string, value?: string[], parentSpan?: OpenTelemetrySpan): Observable<any>;
    public abstract createNewSessionFromIdToken(id_token?: string);
    public abstract getDeviceDisplayCode(): DeviceDisplayCodeData;
    public abstract cancel();
    public abstract getAuthStatus(): Observable<AuthStatus>;
    public abstract sameTabAuthStarted(): Observable<StarfleetTransitionState>;
    public abstract syncSession(session: Session): Observable<Session>;
    public abstract updatePiplConfigUrls(diffConfig: BaseConfigType);
    public abstract get isUserLoggedIn(): Observable<boolean>;
    public abstract get accountStatusChanged(): Observable<StarfleetUser>;
    public abstract get lastError(): ErrorInfo;
    public abstract getStarfleetSession(): Observable<Session>;

    // starfleet auth error, it will refresh the token for 401 response
    public abstract handleAuthError<T>(
        error,
        refreshForExpired: boolean,
        callBackforTokenExpired: (...args) => Observable<T>,
        ...args
    ): Observable<T>;
}
